import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardMedia, Typography, Box, Button, Stack, Chip, Rating } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// FIREBASE IMPORTS
import { getAuth } from "firebase/auth";
import { doc, deleteDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

import FavoriteButton from './FavoriteButton';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const RecipeCard = ({ recipe, isAdmin = false }) => {
  const navigate = useNavigate();
  const defaultImage = "/no-image.png";
  const [userIsAdmin, setUserIsAdmin] = useState(isAdmin);

  useEffect(() => {
    if (isAdmin) {
      setUserIsAdmin(true);
      return;
    }
    const auth = getAuth();
    if (auth.currentUser) {
      getDoc(doc(db, "users", auth.currentUser.uid))
        .then((docSnap) => {
          if (docSnap.exists() && docSnap.data().role === "admin") {
            setUserIsAdmin(true);
          }
        })
        .catch((err) => console.error("Admin check error in RecipeCard:", err));
    }
  }, [isAdmin]);

  if (!recipe) {
    return null;
  }

  const imageToShow = (recipe.images && recipe.images.length > 0 && recipe.images[0]) 
    ? recipe.images[0] 
    : (recipe.image || defaultImage);

  // --- OWNERSHIP CHECK ---
  const auth = getAuth();
  const user = auth.currentUser;
  
  const isOwner = user && recipe.user_id && user.uid === recipe.user_id;
  const canEdit = isOwner || userIsAdmin; 

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/edit-recipe/${recipe.id}`);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    const confirmDelete = window.confirm("Are you sure you want to delete this recipe?");
    if (confirmDelete) {
      try {
        if (!user) return;
        
        // Delete directly from Firestore
        await deleteDoc(doc(db, "recipes", String(recipe.id)));
        
        window.location.reload();
      } catch (err) {
        console.error(err);
        alert("Failed to delete recipe. You might not have permission.");
      }
    }
  };

  return (
    <Card 
      onClick={() => navigate(`/recipes/${recipe.id}`)}
      sx={{ 
        width: '100%',
        height: '100%',
        maxWidth: { xs: 400, sm: 280 }, 
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderRadius: 3, 
        boxShadow: 2, 
        cursor: 'pointer',
        overflow: 'hidden',
        transition: "transform 0.2s, box-shadow 0.2s",
        '&:hover': { transform: "translateY(-4px)", boxShadow: 6 },
        '&:active': { transform: "scale(0.99)" }
      }}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, backgroundColor: 'rgba(255, 255, 255, 0.75)', borderRadius: '50%' }}>
        <FavoriteButton recipeId={recipe.id} isInitiallyFavorited={recipe.is_favorited} />
      </Box>

      {/* Spice Badge */}
      {(recipe.original_recipe_id || recipe.original_recipe) && (
        <Chip 
            icon={<AutoAwesomeIcon style={{ color: '#D28415' }} />} 
            label="Spiced Up"
            size="small"
            sx={{ 
                position: 'absolute', 
                top: 8, 
                left: 8, 
                zIndex: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.92)', 
                fontWeight: 'bold',
                color: '#D28415',
                boxShadow: 1
            }} 
        />
      )}

      {/* Fixed-size image container ensures every card image is identical */}
      <Box sx={{ width: '100%', height: 175, minHeight: 175, maxHeight: 175, position: 'relative', bgcolor: '#f0ede6', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          image={imageToShow}
          alt={recipe.recipe_name}
          onError={(e) => { e.target.src = defaultImage; }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </Box>
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}>
        {/* Title: 2 lines with consistent fixed height */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: "'Patrick Hand', cursive",
            fontSize: '1.25rem',
            lineHeight: 1.25,
            height: '2.5rem',
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: '-webkit-box', 
            WebkitLineClamp: 2, 
            WebkitBoxOrient: 'vertical', 
            mb: 0.75
          }}
        >
          {recipe.recipe_name}
        </Typography>

        {/* Rating Row: Fixed height */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: 22, mb: 0.75 }}>
          <Rating value={recipe.rating || 0} precision={0.5} readOnly size="small" />
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            ({recipe.rating_count || 0})
          </Typography>
        </Box>

        {/* Description: 2 lines with consistent fixed height */}
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontSize: '0.85rem',
            lineHeight: 1.35,
            height: '2.3rem',
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: '-webkit-box', 
            WebkitLineClamp: 2, 
            WebkitBoxOrient: 'vertical',
            mb: 1
          }}
        >
          {recipe.description || "A delicious family recipe."}
        </Typography>

        {/* Spacer pushing footer content to bottom */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Metadata Footer: Total Time and Cuisine */}
        <Box sx={{ pt: 1, borderTop: '1px solid #f0ede6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
            ⏱️ {(recipe.prep_time || 0) + (recipe.cook_time || 0)} mins
          </Typography>
          {recipe.cuisine && recipe.cuisine !== "General" && (
            <Typography variant="caption" sx={{ color: '#D28415', fontWeight: 'bold' }}>
              {recipe.cuisine}
            </Typography>
          )}
        </Box>

        {canEdit && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="outlined" size="small" onClick={handleEdit} sx={{ flex: 1, py: 0.4, minWidth: 'auto', fontSize: '0.75rem' }}>
              Edit
            </Button>
            <Button variant="outlined" color="error" size="small" onClick={handleDelete} sx={{ flex: 1, py: 0.4, minWidth: 'auto', fontSize: '0.75rem' }}>
              Delete
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipeCard;